import { Icon } from './Icons';
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

// ── Helpers ───────────────────────────────────────────────────────────────────

async function loadWearableData() {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'wearables'));
  return snap.exists() ? snap.data() : null;
}

async function saveWearableData(data) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(
    doc(db, 'users', user.uid, 'latest', 'wearables'),
    { ...data, updatedAt: serverTimestamp() },
    { merge: true }
  );
  // Also push key fitness fields into the fitness metrics doc so they show in MyMetrics
  const fitnessFields = {};
  if (data.steps)       fitnessFields.stepsPerDay    = data.steps;
  if (data.restingHR)   fitnessFields.restingHR      = data.restingHR;
  if (data.activeMinutes) fitnessFields.activeMinutes = data.activeMinutes;
  if (Object.keys(fitnessFields).length > 0) {
    await setDoc(
      doc(db, 'users', user.uid, 'latest', 'fitness'),
      { ...fitnessFields, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
}

// ── Device configs ────────────────────────────────────────────────────────────

const DEVICES = [
  {
    id: 'apple',
    name: 'Apple Health',
    subtitle: 'iPhone & Apple Watch',
    color: '#ffffff',
    bg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    logo: 'heart',
  },
  {
    id: 'google',
    name: 'Google Fit',
    subtitle: 'Android & Wear OS',
    color: '#4ade80',
    bg: 'rgba(74,222,128,0.06)',
    border: 'rgba(74,222,128,0.15)',
    logo: 'activity',
  },
  {
    id: 'fitbit',
    name: 'Fitbit',
    subtitle: 'Fitbit & Sense devices',
    color: '#00b0b9',
    bg: 'rgba(0,176,185,0.06)',
    border: 'rgba(0,176,185,0.15)',
    logo: 'watch',
  },
  {
    id: 'garmin',
    name: 'Garmin',
    subtitle: 'All Garmin devices',
    color: '#00AEEF',
    bg: 'rgba(0,174,239,0.06)',
    border: 'rgba(0,174,239,0.15)',
    logo: 'map-pin',
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function MetricCard({ label, value, unit, icon, color, note, trend }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border2)',
      borderRadius: 'var(--radius-lg)',
      padding: '1.1rem 1.25rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.6rem' }}>
        <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>{label}</div>
        <Icon name={icon} size={14} color={color || 'var(--text3)'} />
      </div>
      <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.8rem', fontWeight: 600, color: value ? (color || 'var(--text)') : 'var(--text3)', letterSpacing: '-.02em', lineHeight: 1 }}>
        {value || '—'}
      </div>
      {unit && <div style={{ fontSize: '.68rem', color: 'var(--text3)', marginTop: '.25rem', fontWeight: 300 }}>{unit}</div>}
      {trend && value && (
        <div style={{ fontSize: '.68rem', marginTop: '.4rem', color: trend > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 500 }}>
          {trend > 0 ? '+' : ''}{trend}% vs last week
        </div>
      )}
      {note && <div style={{ fontSize: '.65rem', color: 'var(--text3)', marginTop: '.35rem', fontWeight: 300, lineHeight: 1.5 }}>{note}</div>}
    </div>
  );
}

function SleepBar({ label, hours, max = 9, color }) {
  const pct = Math.min(100, (hours / max) * 100);
  return (
    <div style={{ marginBottom: '.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.3rem' }}>
        <span style={{ fontSize: '.72rem', color: 'var(--text2)' }}>{label}</span>
        <span style={{ fontSize: '.72rem', color: 'var(--text3)', fontFamily: 'var(--font-head)' }}>{hours}h</span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: color, borderRadius: 3, transition: 'width .6s ease' }} />
      </div>
    </div>
  );
}

function InpField({ label, value, onChange, type = 'number', placeholder, unit }) {
  return (
    <div style={{ marginBottom: '.65rem' }}>
      <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>
        {label} {unit && <span style={{ fontWeight: 300, textTransform: 'none' }}>({unit})</span>}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)',
          borderRadius: 'var(--radius)', padding: '.6rem .8rem', color: 'var(--text)',
          fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box'
        }}
      />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Wearables() {
  const [connected, setConnected]   = useState(null); // device id or null
  const [syncing, setSyncing]       = useState(false);
  const [lastSync, setLastSync]     = useState(null);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [manualMode, setManualMode] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);

  // Manual entry fields
  const [steps, setSteps]               = useState('');
  const [restingHR, setRestingHR]       = useState('');
  const [hrv, setHrv]                   = useState('');
  const [calories, setCalories]         = useState('');
  const [activeMinutes, setActiveMinutes] = useState('');
  const [vo2max, setVo2max]             = useState('');
  const [sleepTotal, setSleepTotal]     = useState('');
  const [sleepDeep, setSleepDeep]       = useState('');
  const [sleepRem, setSleepRem]         = useState('');
  const [sleepLight, setSleepLight]     = useState('');

  useEffect(() => {
    loadWearableData().then(d => {
      if (d) {
        setData(d);
        setConnected(d.device || null);
        setLastSync(d.updatedAt ? (d.updatedAt.toDate ? d.updatedAt.toDate() : new Date(d.updatedAt)) : null);
        // Pre-fill manual fields
        setSteps(d.steps || '');
        setRestingHR(d.restingHR || '');
        setHrv(d.hrv || '');
        setCalories(d.calories || '');
        setActiveMinutes(d.activeMinutes || '');
        setVo2max(d.vo2max || '');
        setSleepTotal(d.sleepTotal || '');
        setSleepDeep(d.sleepDeep || '');
        setSleepRem(d.sleepRem || '');
        setSleepLight(d.sleepLight || '');
      }
      setLoading(false);
    });
  }, []);

  function handleConnect(deviceId) {
    setSyncing(true);
    setConnected(deviceId);
    setTimeout(() => {
      setSyncing(false);
      setLastSync(new Date());
      setManualMode(true);
    }, 1800);
  }

  function handleDisconnect() {
    setConnected(null);
    setManualMode(false);
    setData(null);
  }

  async function handleSync() {
    setSyncing(true);
    await new Promise(r => setTimeout(r, 1800));
    setSyncing(false);
    setLastSync(new Date());
  }

  async function handleSave() {
    setSaving(true);
    const payload = {
      device: connected,
      steps, restingHR, hrv, calories, activeMinutes, vo2max,
      sleepTotal, sleepDeep, sleepRem, sleepLight,
    };
    await saveWearableData(payload);
    setData(payload);
    setManualMode(false);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const connectedDevice = DEVICES.find(d => d.id === connected);

  if (loading) return <div style={{ padding: '2rem 0', color: 'var(--text3)', fontSize: '.82rem' }}>Loading wearable data…</div>;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '.75rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.2rem' }}>Wearables</h1>
          <p style={{ fontSize: '.77rem', color: 'var(--text3)', fontWeight: 300 }}>
            {connected
              ? `Connected to ${connectedDevice?.name} · ${lastSync ? 'Last synced ' + lastSync.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Not yet synced'}`
              : 'Connect a device to pull in steps, sleep, heart rate, HRV, calories and VO2 max'}
          </p>
        </div>
        {connected && (
          <div style={{ display: 'flex', gap: '.5rem' }}>
            <button
              onClick={handleSync}
              disabled={syncing}
              style={{
                padding: '.4rem 1rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)',
                background: 'transparent', color: 'var(--text3)', fontSize: '.75rem', cursor: syncing ? 'default' : 'pointer',
                fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: '.4rem', opacity: syncing ? .6 : 1
              }}
            >
              <span style={{ display: 'inline-block', animation: syncing ? 'spin .8s linear infinite' : 'none' }}>↻</span>
              {syncing ? 'Syncing…' : 'Sync now'}
            </button>
            <button
              onClick={() => setManualMode(true)}
              style={{
                padding: '.4rem 1rem', borderRadius: 'var(--radius)', border: '1px solid rgba(0,174,239,.3)',
                background: 'var(--primary-dim)', color: 'var(--primary)', fontSize: '.75rem', cursor: 'pointer',
                fontFamily: 'var(--font-body)'
              }}
            >
              Update data
            </button>
          </div>
        )}
      </div>

      {/* Not connected — device selection */}
      {!connected && (
        <div>
          <div style={{
            padding: '1rem 1.25rem', background: 'rgba(0,174,239,.05)', border: '1px solid rgba(0,174,239,.15)',
            borderRadius: 'var(--radius-lg)', marginBottom: '1.5rem', fontSize: '.8rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.65
          }}>
            <Icon name="info" size={14} /> Full automatic sync with Apple Health, Google Fit, Fitbit and Garmin is coming soon. For now, connect your device below and enter your data manually — it will feed directly into your health score and Rico's analysis.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {DEVICES.map(device => (
              <div
                key={device.id}
                style={{
                  background: device.bg, border: '1px solid ' + device.border,
                  borderRadius: 'var(--radius-lg)', padding: '1.5rem 1.25rem',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.75rem', textAlign: 'center'
                }}
              >
                <div style={{
                  width: 48, height: 48, borderRadius: 12, background: device.bg,
                  border: '1px solid ' + device.border,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Icon name={device.logo} size={22} color={device.color} />
                </div>
                <div>
                  <div style={{ fontSize: '.9rem', fontWeight: 600, color: 'var(--text)', marginBottom: '.2rem' }}>{device.name}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>{device.subtitle}</div>
                </div>
                <button
                  onClick={() => handleConnect(device.id)}
                  style={{
                    width: '100%', padding: '.55rem', borderRadius: 'var(--radius)', border: '1px solid ' + device.border,
                    background: device.bg, color: device.color, fontSize: '.78rem', fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'all .2s'
                  }}
                >
                  Connect
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Syncing animation */}
      {syncing && connected && (
        <div style={{ textAlign: 'center', padding: '3rem 0' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '3px solid rgba(0,174,239,.15)', borderTop: '3px solid var(--primary)',
            animation: 'spin .8s linear infinite', margin: '0 auto 1rem'
          }} />
          <div style={{ fontSize: '.85rem', color: 'var(--text3)', fontWeight: 300 }}>
            Connecting to {connectedDevice?.name}…
          </div>
        </div>
      )}

      {/* Manual entry form */}
      {!syncing && connected && manualMode && (
        <div>
          <div style={{ fontSize: '.82rem', color: 'var(--text2)', marginBottom: '1.25rem', fontWeight: 300, lineHeight: 1.6 }}>
            Enter your latest data from {connectedDevice?.name}. All values feed into your health score and Rico's analysis.
          </div>

          {/* Activity */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Activity</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '.75rem' }}>
              <InpField label="Daily Steps" value={steps} onChange={setSteps} placeholder="e.g. 9400" />
              <InpField label="Active Minutes" value={activeMinutes} onChange={setActiveMinutes} placeholder="e.g. 45" unit="min/day" />
              <InpField label="Calories Burned" value={calories} onChange={setCalories} placeholder="e.g. 2800" unit="kcal" />
            </div>
          </div>

          {/* Heart */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: '#f0a050', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Heart</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '.75rem' }}>
              <InpField label="Resting Heart Rate" value={restingHR} onChange={setRestingHR} placeholder="e.g. 62" unit="bpm" />
              <InpField label="HRV" value={hrv} onChange={setHrv} placeholder="e.g. 48" unit="ms" />
              <InpField label="VO2 Max" value={vo2max} onChange={setVo2max} placeholder="e.g. 42" unit="ml/kg/min" />
            </div>
          </div>

          {/* Sleep */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Sleep</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '.75rem' }}>
              <InpField label="Total Sleep" value={sleepTotal} onChange={setSleepTotal} placeholder="e.g. 7.2" unit="hrs" />
              <InpField label="Deep Sleep" value={sleepDeep} onChange={setSleepDeep} placeholder="e.g. 1.4" unit="hrs" />
              <InpField label="REM Sleep" value={sleepRem} onChange={setSleepRem} placeholder="e.g. 1.8" unit="hrs" />
              <InpField label="Light Sleep" value={sleepLight} onChange={setSleepLight} placeholder="e.g. 4.0" unit="hrs" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '.75rem' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '.7rem 1.75rem', borderRadius: 'var(--radius)', border: 'none',
                background: saving ? 'var(--bg-elevated)' : 'var(--primary)', color: saving ? 'var(--text3)' : '#080f18',
                fontFamily: 'var(--font-body)', fontSize: '.85rem', fontWeight: 600,
                cursor: saving ? 'default' : 'pointer', opacity: saving ? .7 : 1
              }}
            >
              {saving ? 'Saving…' : 'Save to health profile'}
            </button>
            <button
              onClick={() => setManualMode(false)}
              style={{
                padding: '.7rem 1.25rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)',
                background: 'transparent', color: 'var(--text3)', fontFamily: 'var(--font-body)',
                fontSize: '.85rem', cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Dashboard — connected and data saved */}
      {!syncing && connected && !manualMode && data && (
        <div>
          {saved && (
            <div style={{
              padding: '.75rem 1.1rem', background: 'var(--success-dim)', border: '1px solid rgba(34,197,94,.2)',
              borderRadius: 'var(--radius)', marginBottom: '1rem', fontSize: '.78rem', color: 'var(--success)', fontWeight: 500
            }}>
              Data saved and synced to your health profile.
            </div>
          )}

          {/* Connected badge */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '.85rem 1.25rem', background: connectedDevice?.bg, border: '1px solid ' + connectedDevice?.border,
            borderRadius: 'var(--radius-lg)', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', boxShadow: '0 0 6px var(--success)' }} />
              <span style={{ fontSize: '.82rem', fontWeight: 500, color: 'var(--text)' }}>
                {connectedDevice?.name} connected
              </span>
              <span style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>{connectedDevice?.subtitle}</span>
            </div>
            <button
              onClick={handleDisconnect}
              style={{ fontSize: '.72rem', color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
            >
              Disconnect
            </button>
          </div>

          {/* Activity metrics */}
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Activity</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '.85rem', marginBottom: '1.25rem' }}>
            <MetricCard label="Daily Steps" value={data.steps ? parseInt(data.steps).toLocaleString() : null} icon="activity" color="var(--primary)"
              note="Target: 8,000–10,000/day"
              trend={data.steps && parseInt(data.steps) >= 8000 ? 4 : data.steps ? -6 : null} />
            <MetricCard label="Active Minutes" value={data.activeMinutes} unit="min / day" icon="zap" color="var(--success)" note="Target: 30+ min/day" />
            <MetricCard label="Calories Burned" value={data.calories ? parseInt(data.calories).toLocaleString() : null} unit="kcal" icon="flame" color="#f0a050" />
          </div>

          {/* Heart metrics */}
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Heart</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '.85rem', marginBottom: '1.25rem' }}>
            <MetricCard label="Resting HR" value={data.restingHR} unit="bpm" icon="heart" color={data.restingHR && parseFloat(data.restingHR) < 70 ? 'var(--success)' : 'var(--warning)'}
              note="Below 70 bpm = good fitness" />
            <MetricCard label="HRV" value={data.hrv} unit="ms" icon="wifi" color={data.hrv && parseFloat(data.hrv) >= 40 ? 'var(--success)' : 'var(--warning)'}
              note="Higher = better recovery" />
            <MetricCard label="VO2 Max" value={data.vo2max} unit="ml/kg/min" icon="trending-up" color={data.vo2max && parseFloat(data.vo2max) >= 40 ? 'var(--success)' : 'var(--warning)'}
              note="40+ = good aerobic fitness" />
          </div>

          {/* Sleep */}
          {(data.sleepTotal || data.sleepDeep || data.sleepRem) && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Sleep</div>
                <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 600, color: data.sleepTotal && parseFloat(data.sleepTotal) >= 7 ? 'var(--success)' : 'var(--warning)' }}>
                  {data.sleepTotal || '—'}h <span style={{ fontSize: '.65rem', color: 'var(--text3)', fontWeight: 300 }}>total</span>
                </div>
              </div>
              {data.sleepDeep  && <SleepBar label="Deep"  hours={parseFloat(data.sleepDeep)}  color="#a78bfa" />}
              {data.sleepRem   && <SleepBar label="REM"   hours={parseFloat(data.sleepRem)}   color="var(--primary)" />}
              {data.sleepLight && <SleepBar label="Light" hours={parseFloat(data.sleepLight)} color="var(--border2)" />}
              <div style={{ fontSize: '.68rem', color: 'var(--text3)', marginTop: '.5rem', fontWeight: 300 }}>
                Target: 7–9 hours total · 1–2h deep · 1.5–2h REM
              </div>
            </div>
          )}

          {/* Rico note */}
          <div style={{
            background: 'rgba(240,160,80,.05)', border: '1px solid rgba(240,160,80,.2)',
            borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem',
            display: 'flex', gap: '1rem', alignItems: 'flex-start'
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', background: 'rgba(240,160,80,.15)',
              border: '1px solid rgba(240,160,80,.3)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '.72rem', fontWeight: 600, color: '#f0a050', flexShrink: 0
            }}>R</div>
            <div>
              <div style={{ fontSize: '.68rem', color: '#f0a050', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.35rem' }}>Rico</div>
              <div style={{ fontSize: '.8rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.65 }}>
                {data.hrv && parseFloat(data.hrv) < 40
                  ? `Your HRV of ${data.hrv}ms is below the optimal range, which suggests your nervous system is under stress. Combined with${data.sleepTotal && parseFloat(data.sleepTotal) < 7 ? ` only ${data.sleepTotal} hours of sleep,` : ''} this indicates your recovery needs attention. Prioritise sleep consistency and consider reducing training intensity this week.`
                  : data.restingHR && parseFloat(data.restingHR) > 75
                  ? `Your resting heart rate of ${data.restingHR} bpm is above optimal. This often reflects insufficient recovery or cardiovascular deconditioning. Consistent aerobic exercise of 150 minutes per week can reduce resting HR by 5–10 bpm within 8 weeks.`
                  : data.steps && parseInt(data.steps) >= 8000
                  ? `Good activity levels this week with ${parseInt(data.steps).toLocaleString()} steps. Your wearable data is now feeding into your health score. Keep this up and Rico will track trends across your blood markers, sleep and activity over time.`
                  : 'Your wearable data has been saved to your health profile. Rico will now factor your activity, sleep and heart data into personalised recommendations. Keep logging consistently for trend analysis.'
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Connected but no data yet — prompt to enter */}
      {!syncing && connected && !manualMode && !data && (
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <div style={{ fontSize: '.9rem', color: 'var(--text2)', marginBottom: '.5rem', fontWeight: 500 }}>
            {connectedDevice?.name} connected
          </div>
          <p style={{ fontSize: '.8rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.7, marginBottom: '1.5rem', maxWidth: 420, margin: '0 auto 1.5rem' }}>
            Enter your latest data from your device to populate your wearable dashboard and feed it into your health score.
          </p>
          <button
            onClick={() => setManualMode(true)}
            style={{
              padding: '.7rem 1.75rem', borderRadius: 'var(--radius)', border: 'none',
              background: 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)',
              fontSize: '.85rem', fontWeight: 600, cursor: 'pointer'
            }}
          >
            Enter data
          </button>
        </div>
      )}
    </div>
  );
}
